import React from 'react'
import { createContext } from 'lib/createContext'
import { Loading, Message } from '@workwhile/ui'
import { parseErrorMessage } from 'api'
import { useDayforceWebhooksQuery } from 'queries/admin/dayforce/useDayforceWebhooksQuery'
import { usePaginationQueryState } from 'lib/nuqs/usePaginationQueryState'
import { useDayforceWebhooksFiltersQueryState } from '../hooks/useDayforceWebhooksFiltersQueryState'
import { useDayforceAgenciesContextValue } from '../../shared/context/DayforceAgenciesProvider'
import { DayforceAgency } from 'api/admin/dayforce/listDayforceAgencies'

type DayforceWebhooksContextState = {
  state: {
    data: NonNullable<ReturnType<typeof useDayforceWebhooksQuery>['data']>
    dataUpdatedAt: number
    isFetching: boolean
    isError: boolean
    error: string
    pagination: {
      pageIndex: number
      pageSize: number
    }
    agencies: Record<string, DayforceAgency>
    filters: {
      agencyId: string[] | null
      eventType: string[] | null
      status: string[] | null
    }
  }
  actions: {
    refetch: () => void
    setPagination: ReturnType<typeof usePaginationQueryState>[1]
    setFilters: ReturnType<typeof useDayforceWebhooksFiltersQueryState>[1]
  }
}

const [Context, useDayforceWebhooksContextValue] =
  createContext<DayforceWebhooksContextState>({
    name: 'DayforceWebhooksContext',
  })

export const DayforceWebhooksProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const {
    state: { agencies },
  } = useDayforceAgenciesContextValue()

  const [pagination, setPagination] = usePaginationQueryState()
  const [filters, setFilters] = useDayforceWebhooksFiltersQueryState()

  const {
    data,
    isPending,
    isError,
    error,
    dataUpdatedAt,
    refetch,
    isFetching,
  } = useDayforceWebhooksQuery({
    page: pagination.pageIndex,
    pageSize: pagination.pageSize,
    agencyId: filters.agencyId,
    eventType: filters.eventType,
    status: filters.status,
  })

  if (isPending) {
    return <Loading />
  }

  if (isError && !data) {
    // eslint-disable-next-line no-console
    console.error(error)
    return (
      <Message variant="error" m={4} borderRadius="small">
        Sorry, something went wrong. Please reach out to eng support with the
        URL of the page if this persists.
      </Message>
    )
  }

  if (!data) {
    return null
  }

  const value = {
    state: {
      data,
      dataUpdatedAt,
      isFetching,
      isError,
      error: parseErrorMessage(error),
      pagination,
      agencies,
      filters,
    },
    actions: {
      refetch,
      setPagination,
      setFilters,
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useDayforceWebhooksContextValue }
